// F3a GREEN — StructuredAddressSection. Ported from
// deprecated-business@16fd6b71 src/components/address/StructuredAddressSection.tsx
// and rebuilt with MUI form controls. Google Maps verification is delegated to
// the existing `@bw/shared` maps primitives — we never talk to Google directly
// here.
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";

import {
  buildAddressLookupQuery,
  geocodeAddress,
  getGoogleMapsApiKey,
} from "@bw/shared/components";
import type { AddressLookupInput, AddressResult } from "@bw/shared/components";
import type { Address } from "@bw/shared/types";
import { MaterialAddressAutocomplete } from "./MaterialAddressAutocomplete";

export interface AddressDraft {
  formattedAddress: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  source: "google" | "manual";
}

export type VerifiedAddress = Omit<Address, "latitude" | "longitude"> & {
  latitude: number;
  longitude: number;
};

export interface StructuredAddressSectionProps {
  value: AddressDraft;
  onChange: (next: AddressDraft) => void;
  idPrefix: string;
  autocompleteLabel: string;
  autocompletePlaceholder: string;
  manualLine1Label: string;
  helperText?: string;
  onAddressVerified?: (result: AddressResult) => void;
  onUseBusinessAddress?: () => void;
  disabled?: boolean;
  embedded?: boolean;
}

function normalizeCountry(country?: string | null): string {
  return country?.trim() || "US";
}

function buildLookupInput(value: AddressDraft): AddressLookupInput {
  return {
    line1: value.line1,
    city: value.city,
    state: value.state,
    postal_code: value.postalCode,
    country: normalizeCountry(value.country),
  };
}

function clearVerification(nextDraft: AddressDraft): AddressDraft {
  return {
    ...nextDraft,
    formattedAddress: "",
    latitude: null,
    longitude: null,
    source: "manual",
  };
}

function applyVerifiedAddress(
  previous: AddressDraft,
  result: AddressResult,
): AddressDraft {
  return {
    ...previous,
    formattedAddress: result.formatted_address,
    line1: result.address_line1,
    city: result.city,
    state: result.state,
    postalCode: result.postal_code,
    country: normalizeCountry(result.country),
    latitude: result.lat,
    longitude: result.lng,
    source: "google",
  };
}

// Permissive seed input: every field is optional + nullable so any backend
// address shape (OrgAddress, FacilityAddress, the registration draft) seeds a
// draft without a cast. The body normalizes nulls to "" / null below.
export interface AddressDraftInput {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export function createAddressDraft(
  address?: AddressDraftInput | null,
  formattedAddress?: string | null,
): AddressDraft {
  return {
    formattedAddress: formattedAddress?.trim() ?? "",
    line1: address?.line1 ?? "",
    line2: address?.line2 ?? "",
    city: address?.city ?? "",
    state: address?.state ?? "",
    postalCode: address?.postal_code ?? "",
    country: normalizeCountry(address?.country),
    latitude: address?.latitude ?? null,
    longitude: address?.longitude ?? null,
    source:
      formattedAddress ||
      address?.latitude != null ||
      address?.longitude != null
        ? "google"
        : "manual",
  };
}

export function hasAnyAddressInput(value: AddressDraft): boolean {
  return Boolean(
    value.line1.trim() ||
      value.line2.trim() ||
      value.city.trim() ||
      value.state.trim() ||
      value.postalCode.trim(),
  );
}

export function isAddressDraftComplete(value: AddressDraft): boolean {
  return Boolean(
    value.line1.trim() &&
      value.city.trim() &&
      value.state.trim() &&
      value.postalCode.trim(),
  );
}

export function isAddressDraftVerified(value: AddressDraft): boolean {
  return Boolean(
    isAddressDraftComplete(value) &&
      value.latitude != null &&
      value.longitude != null,
  );
}

export function hasSameAddressDefinition(
  first: AddressDraft,
  second: AddressDraft,
): boolean {
  return (
    first.line1 === second.line1 &&
    first.city === second.city &&
    first.state === second.state &&
    first.postalCode === second.postalCode &&
    normalizeCountry(first.country) === normalizeCountry(second.country)
  );
}

export function composeAddressLocation(value: AddressDraft): string {
  const manualLocation = [
    value.line1.trim(),
    value.line2.trim(),
    value.city.trim(),
    value.state.trim(),
    value.postalCode.trim(),
    normalizeCountry(value.country),
  ]
    .filter(Boolean)
    .join(", ");

  if (value.line2.trim()) {
    return manualLocation;
  }
  return value.formattedAddress.trim() || manualLocation;
}

export function addressDraftToAddress(
  value: AddressDraft,
): VerifiedAddress | null {
  const { latitude, longitude } = value;
  if (!isAddressDraftVerified(value) || latitude == null || longitude == null) {
    return null;
  }

  return {
    line1: value.line1.trim(),
    line2: value.line2.trim() || null,
    city: value.city.trim(),
    state: value.state.trim() || null,
    postal_code: value.postalCode.trim(),
    country: normalizeCountry(value.country),
    latitude,
    longitude,
  };
}

function getSearchValue(value: AddressDraft): string {
  if (value.formattedAddress.trim()) {
    return value.formattedAddress.trim();
  }
  if (isAddressDraftComplete(value)) {
    return buildAddressLookupQuery(buildLookupInput(value));
  }
  return "";
}

export function StructuredAddressSection({
  value,
  onChange,
  idPrefix,
  autocompleteLabel,
  autocompletePlaceholder,
  manualLine1Label,
  helperText,
  onAddressVerified,
  onUseBusinessAddress,
  disabled = false,
  embedded = false,
}: StructuredAddressSectionProps) {
  const googleMapsKey = getGoogleMapsApiKey();
  const googleEnabled = Boolean(googleMapsKey);
  const [searchValue, setSearchValue] = useState(() => getSearchValue(value));
  const [verifyError, setVerifyError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const valueRef = useRef(value);
  const searchValueRef = useRef(searchValue);
  const manualVerificationVersionRef = useRef(0);
  valueRef.current = value;
  searchValueRef.current = searchValue;

  useEffect(() => {
    const nextSearchValue = getSearchValue(value);
    searchValueRef.current = nextSearchValue;
    setSearchValue(nextSearchValue);
  }, [
    value.city,
    value.country,
    value.formattedAddress,
    value.line1,
    value.postalCode,
    value.state,
  ]);

  const addressComplete = isAddressDraftComplete(value);
  const addressVerified = isAddressDraftVerified(value);
  const verifyQuery =
    searchValue.trim() ||
    (addressComplete ? buildAddressLookupQuery(buildLookupInput(value)) : "");
  const canVerify = Boolean(googleEnabled && verifyQuery);
  const statusLabel = addressVerified
    ? "Verified Address"
    : "Address Needs Verification";

  function updateField(
    field: keyof AddressDraft,
    nextValue: string | number | null,
  ) {
    let nextDraft: AddressDraft = {
      ...valueRef.current,
      [field]: nextValue,
    };

    // Editing any address-defining field clears the Google match. Line 2
    // (suite/unit) is metadata only and must NOT un-verify the base address.
    if (field !== "line2") {
      manualVerificationVersionRef.current += 1;
      setIsVerifying(false);
      nextDraft = clearVerification(nextDraft);
    }

    valueRef.current = nextDraft;
    setVerifyError("");
    onChange(nextDraft);
  }

  function handleSearchValueChange(nextSearchValue: string) {
    if (nextSearchValue !== searchValueRef.current) {
      manualVerificationVersionRef.current += 1;
      setIsVerifying(false);
      if (isAddressDraftVerified(valueRef.current)) {
        const nextDraft = {
          ...valueRef.current,
          latitude: null,
          longitude: null,
          source: "manual" as const,
        };
        valueRef.current = nextDraft;
        onChange(nextDraft);
      }
    }
    searchValueRef.current = nextSearchValue;
    setSearchValue(nextSearchValue);
    setVerifyError("");
  }

  function handleVerifiedResult(
    result: AddressResult,
    previous = valueRef.current,
  ) {
    setVerifyError("");
    searchValueRef.current = result.formatted_address;
    setSearchValue(result.formatted_address);
    const nextDraft = applyVerifiedAddress(previous, result);
    valueRef.current = nextDraft;
    onChange(nextDraft);
    onAddressVerified?.(result);
  }

  function handleAutocompleteResult(result: AddressResult) {
    manualVerificationVersionRef.current += 1;
    setIsVerifying(false);
    handleVerifiedResult(result);
  }

  async function handleVerifyAddress() {
    if (disabled || !canVerify) {
      return;
    }

    const requestVersion = manualVerificationVersionRef.current + 1;
    manualVerificationVersionRef.current = requestVersion;
    const draftSnapshot = { ...valueRef.current };
    const querySnapshot = verifyQuery;
    setIsVerifying(true);
    setVerifyError("");
    try {
      const result = await geocodeAddress(querySnapshot, googleMapsKey);
      if (
        manualVerificationVersionRef.current !== requestVersion ||
        !hasSameAddressDefinition(valueRef.current, draftSnapshot)
      ) {
        return;
      }
      handleVerifiedResult(result, valueRef.current);
    } catch {
      if (manualVerificationVersionRef.current !== requestVersion) {
        return;
      }
      setVerifyError(
        "Google could not verify that place or address. Refine the search or structured fields, then try again.",
      );
    } finally {
      if (manualVerificationVersionRef.current === requestVersion) {
        setIsVerifying(false);
      }
    }
  }

  return (
    <Box
      component="section"
      data-testid="structured-address-section"
      sx={{
        bgcolor: embedded ? "transparent" : "background.paper",
        border: embedded ? 0 : 1,
        borderColor: "divider",
        borderRadius: embedded ? 0 : 2,
        boxShadow: embedded ? 0 : 1,
        p: embedded ? 0 : { xs: 2, sm: 2.5 },
      }}
    >
      <Stack spacing={3}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={1.5}
          sx={{
            alignItems: { lg: "flex-start" },
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              component="h3"
              variant="subtitle2"
              sx={{ fontWeight: 700 }}
            >
              {autocompleteLabel}
            </Typography>
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ maxWidth: 672, mt: 0.5 }}
            >
              {helperText ??
                "Search by business or facility name, or type the address directly. We match the base address to Google, save coordinates, and keep suite or unit details editable."}
            </Typography>
          </Box>
          <Chip
            color={addressVerified ? "success" : "warning"}
            label={statusLabel}
            size="small"
            variant="outlined"
          />
        </Stack>

        <Stack spacing={1.5}>
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) auto" },
              alignItems: { lg: "end" },
            }}
          >
            <MaterialAddressAutocomplete
              disabled={disabled || !googleEnabled}
              id={`${idPrefix}-lookup`}
              label={`${autocompleteLabel} Search`}
              onAddressSelect={handleAutocompleteResult}
              onValueChange={handleSearchValueChange}
              placeholder={autocompletePlaceholder}
              value={searchValue}
            />
            <Button
              disabled={disabled || !canVerify || isVerifying}
              onClick={() => void handleVerifyAddress()}
              size="small"
              type="button"
              variant="contained"
            >
              {isVerifying ? "Verifying..." : "Verify Address"}
            </Button>
          </Box>

          {onUseBusinessAddress ? (
            <Box>
              <Button
                disabled={disabled}
                onClick={() => onUseBusinessAddress()}
                size="small"
                type="button"
                variant="outlined"
              >
                Use business address
              </Button>
            </Box>
          ) : null}

          <Typography color="text.secondary" variant="body2">
            Search by venue name or street address. Keep suite or unit details
            in Address Line 2.
          </Typography>

          {!googleEnabled ? (
            <Alert severity="warning" variant="outlined">
              Google address verification is unavailable right now. Saving stays
              blocked until the address can be matched and coordinates are
              attached.
            </Alert>
          ) : null}

          {verifyError ? (
            <Alert
              aria-live="polite"
              role="alert"
              severity="error"
              variant="outlined"
            >
              {verifyError}
            </Alert>
          ) : null}
        </Stack>

        <Divider />

        <Stack spacing={2}>
          <Box>
            <Typography
              component="h4"
              variant="subtitle2"
              sx={{ fontWeight: 700 }}
            >
              Address details
            </Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
              The base address must match Google. Suite, unit, and access notes
              stay manual.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "1fr",
                lg: "minmax(0, 1.15fr) minmax(240px, 0.85fr)",
              },
            }}
          >
            <TextField
              disabled={disabled}
              id={`${idPrefix}-line1`}
              label={manualLine1Label}
              onChange={(event) => updateField("line1", event.target.value)}
              placeholder="Street address"
              value={value.line1}
            />
            <TextField
              disabled={disabled}
              id={`${idPrefix}-line2`}
              label="Address Line 2"
              onChange={(event) => updateField("line2", event.target.value)}
              placeholder="Suite, unit, building, or access notes"
              value={value.line2}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            <TextField
              disabled={disabled}
              id={`${idPrefix}-city`}
              label="City"
              onChange={(event) => updateField("city", event.target.value)}
              placeholder="City"
              value={value.city}
            />
            <TextField
              disabled={disabled}
              id={`${idPrefix}-state`}
              label="State"
              onChange={(event) => updateField("state", event.target.value)}
              placeholder="State"
              value={value.state}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            <TextField
              disabled={disabled}
              id={`${idPrefix}-postal-code`}
              label="Postal Code"
              onChange={(event) =>
                updateField("postalCode", event.target.value)
              }
              placeholder="Postal code"
              value={value.postalCode}
            />
            <TextField
              disabled
              helperText="Business onboarding is currently US-only."
              id={`${idPrefix}-country`}
              label="Country"
              slotProps={{ htmlInput: { readOnly: true } }}
              value={normalizeCountry(value.country)}
            />
          </Box>

          {addressVerified ? (
            <Typography color="text.secondary" variant="body2">
              Changing the street, city, state, or ZIP will clear verification
              and require a fresh match.
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}
